import { BarChart3, CheckCircle2, Clock, ShieldAlert, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { kgToMt, formatNumber, percent } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { Contract, OrderDetail } from "@/types/domain"

type Tone = "info" | "warning" | "success" | "danger" | "muted"

const toneStyles: Record<Tone, { text: string; iconBg: string; iconText: string }> = {
  info: {
    text: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-100 dark:bg-sky-500/15",
    iconText: "text-sky-600 dark:text-sky-300",
  },
  warning: {
    text: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconText: "text-amber-600 dark:text-amber-300",
  },
  success: {
    text: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconText: "text-emerald-600 dark:text-emerald-300",
  },
  danger: {
    text: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-100 dark:bg-red-500/15",
    iconText: "text-red-600 dark:text-red-300",
  },
  muted: {
    text: "text-foreground",
    iconBg: "bg-muted",
    iconText: "text-muted-foreground",
  },
}

interface CardItem {
  label: string
  value: string
  hint?: string
  icon: React.ComponentType<{ className?: string }>
  tone: Tone
}

function SummaryCard({ label, value, hint, icon: Icon, tone }: CardItem) {
  const style = toneStyles[tone]
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{label}</p>
          <p className={cn("text-2xl font-semibold", style.text)}>{value}</p>
          {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
        </div>
        <div className={cn("rounded-lg p-2", style.iconBg)}>
          <Icon className={cn("size-5", style.iconText)} />
        </div>
      </CardContent>
    </Card>
  )
}

interface OrderSummaryCardsProps {
  order: OrderDetail
  contract?: Contract | null
}

export function OrderSummaryCards({ order, contract }: OrderSummaryCardsProps) {
  const { t } = useTranslation(["orders", "common"])

  const totalMt = kgToMt(order.agreedQuantity ?? 0)
  const contractQty = contract?.quantity ?? 0
  const contractPercent = percent(totalMt, contractQty)

  const lockedDeliveries = (order.DeliveryForms ?? []).filter((d) => d.isLocked)
  const lockedKg = lockedDeliveries.reduce(
    (sum, d) => sum + (d.netKilogramsAttached ?? 0),
    0,
  )
  const remainingMt = Math.max(totalMt - kgToMt(lockedKg), 0)

  const traceability = order.traceabilityLevel
  const traceabilityTone: Tone =
    traceability === null || traceability === undefined
      ? "warning"
      : traceability === 100
        ? "success"
        : "danger"
  const TraceabilityIcon =
    traceability === null || traceability === undefined
      ? Clock
      : traceability === 100
        ? CheckCircle2
        : ShieldAlert

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        label={t("kpi.totalQuantity")}
        value={`${formatNumber(totalMt)} ${t("units.mt", { ns: "common" })}`}
        hint={
          contractQty
            ? t("kpi.ofContract", { percent: formatNumber(contractPercent) })
            : undefined
        }
        icon={BarChart3}
        tone="info"
      />
      <SummaryCard
        label={t("kpi.remaining")}
        value={`${formatNumber(remainingMt)} ${t("units.mt", { ns: "common" })}`}
        hint={t("kpi.deliveredCount", { count: lockedDeliveries.length })}
        icon={Truck}
        tone="warning"
      />
      <SummaryCard
        label={t("kpi.traceability")}
        value={
          traceability === null || traceability === undefined
            ? t("kpi.inProgress")
            : `${formatNumber(traceability)} %`
        }
        icon={TraceabilityIcon}
        tone={traceabilityTone}
      />
      <SummaryCard
        label={t("kpi.eudr")}
        value={t("kpi.inProgress")}
        icon={Clock}
        tone="muted"
      />
    </div>
  )
}
